import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar.tsx";
import { AppSidebar } from "@/modules/layout/components/AppSidebar.tsx";
import type { CSSProperties, PropsWithChildren } from "react";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { Loader } from "@/components/Loader.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { t } from "@lingui/core/macro";
import { Toaster } from "@/components/ui/sonner.tsx";
import { useBankData } from "@/dataProviders/BankDataProvider.tsx";
import { useMeData } from "@/dataProviders/MeDataProvider.tsx";

export function Layout({ children }: PropsWithChildren) {
  const { isPending: isConfigPending, isError: isConfigError } = useAppConfig();
  const { isPending: isBanksPending, isError: isBanksError, bankData } = useBankData();
  const { isPending: isMePending, isError: isMeError } = useMeData();

  if (isConfigPending) return <Loader message={t`Loading app's configuration...`} />;
  if (isConfigError)
    return (
      <FetchingError
        message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
      />
    );

  if (isBanksPending) return <Loader message={t`Loading payment providers's data...`} />;
  if (isBanksError)
    return (
      <FetchingError
        message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
      />
    );

  if (isMePending) return <Loader message={t`Loading user's data...`} />;
  if (isMeError)
    return (
      <FetchingError
        message={t`Something went wrong. We were unable to load the user data. Please refresh the page and try again.`}
      />
    );

  return (
    <div>
      <SidebarProvider
        style={
          {
            "--sidebar-width": "calc(var(--spacing) * 72)",
            "--header-height": "calc(var(--spacing) * 12)",
          } as CSSProperties
        }
      >
        {bankData && (
          <>
            <AppSidebar variant={"inset"} />
            <SidebarInset className={"overflow-hidden"}>{children}</SidebarInset>
            <Toaster position="top-right" richColors />
          </>
        )}
      </SidebarProvider>
    </div>
  );
}
